// Initialize Telegram Analytics for tapps store listing
// Configure via VITE_TELEGRAM_ANALYTICS_TOKEN and VITE_TELEGRAM_ANALYTICS_APP_NAME env vars 
import telegramAnalytics from '@telegram-apps/analytics';

// Only initialize analytics if both token and appName are configured
const analyticsToken = import.meta.env.VITE_TELEGRAM_ANALYTICS_TOKEN;
const analyticsAppName = import.meta.env.VITE_TELEGRAM_ANALYTICS_APP_NAME;

if (analyticsToken && analyticsAppName) {
    try {
        telegramAnalytics.init({
            token: analyticsToken,
            appName: analyticsAppName,
        });
    } catch (error) {
        console.warn('Failed to initialize Telegram Analytics:', error);
    }
}

// Initialize Telegram WebApp
function initTelegramWebApp() {
    const tg = window.Telegram.WebApp;
    tg.expand();
    tg.ready();
    return tg;
}

// Wait for Telegram SDK to be ready
let tg;
if (window.Telegram && window.Telegram.WebApp) {
    tg = initTelegramWebApp();
} else {
    // Wait for mock to load
    document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
            if (window.Telegram && window.Telegram.WebApp) {
                tg = initTelegramWebApp();
                initApp();
            }
        }, 100);
    });
}

// State management
const state = {
    credits: 0,
    names: [],
    selectedNames: [],
    currentProof: null,
    provablyFairEnabled: false,
    publicEntrantsEnabled: false,
    adsgramLoaded: false,
    isSpinning: false,
    isWatchingAd: false,
    wheelRotation: 0,
    isPro: false,
    canWatchAd: true,
    adBlockReason: null,
    adMetadata: { hasWatchedAd: false, creditsUsed: 0 }
};

// Constants
const MAX_NAMES = 100;
const MAX_WINNERS = 100;

// Credit purchase configuration
const CREDITS_PURCHASE_AMOUNT = 10; // Credits received when purchasing

// Polling configuration for payment confirmation
const PAYMENT_POLL_CONFIG = {
    maxPolls: 30,      // Poll for up to 30 seconds
    pollInterval: 1000 // Poll every 1 second
};

// Wheel colors (vibrant and diverse)
const WHEEL_COLORS = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
    '#F8B500', '#00CED1', '#FF69B4', '#32CD32', '#FF7F50'
];

// DOM elements
const elements = {
    drawTabBtn: document.getElementById('draw-tab-btn'),
    verifyTabBtn: document.getElementById('verify-tab-btn'),
    drawPanel: document.getElementById('draw-panel'),
    verifyPanel: document.getElementById('verify-panel'),
    namesInput: document.getElementById('names-input'),
    importFileBtn: document.getElementById('import-file-btn'),
    importClipboardBtn: document.getElementById('import-clipboard-btn'),
    importFileInput: document.getElementById('import-file-input'),
    nameCount: document.getElementById('name-count'),
    wheelCanvas: document.getElementById('wheel-canvas'),
    wheelContainer: document.getElementById('wheel-container'),
    wheelPointer: document.querySelector('.wheel-pointer'),
    pickCount: document.getElementById('pick-count'),
    provablyFairToggle: document.getElementById('provably-fair-toggle'),
    publicEntrantsToggle: document.getElementById('public-entrants-toggle'),
    provablyFairStatus: document.getElementById('provably-fair-status'),
    spinBtn: document.getElementById('spin-btn'),
    verifyReceiptInput: document.getElementById('verify-receipt-input'),
    verifyReceiptBtn: document.getElementById('verify-receipt-btn'),
    verifyResult: document.getElementById('verify-result'),
    resultsSection: document.getElementById('results-section'),
    resultsList: document.getElementById('results-list'),
    copyBtn: document.getElementById('copy-btn'),
    shareBtn: document.getElementById('share-btn'),
    sendToChatBtn: document.getElementById('send-to-chat-btn'),
    proofSection: document.getElementById('proof-section'),
    proofSummary: document.getElementById('proof-summary'),
    toggleProofBtn: document.getElementById('toggle-proof-btn'),
    replayDrawBtn: document.getElementById('replay-draw-btn'),
    copyProofBtn: document.getElementById('copy-proof-btn'),
    proofJson: document.getElementById('proof-json'),
    pickAgainBtn: document.getElementById('pick-again-btn'),
    removeWinnersBtn: document.getElementById('remove-winners-btn'),
    startFreshBtn: document.getElementById('start-fresh-btn'),
    watchAdBtn: document.getElementById('watch-ad-btn'),
    buyCreditsBtn: document.getElementById('buy-credits-btn'),
    unlockProBtn: document.getElementById('unlock-pro-btn'),
    earnCreditsSection: document.getElementById('earn-credits-section'),
    creditsCount: document.getElementById('credits-count'),
    loadingOverlay: document.getElementById('loading-overlay'),
    loadingText: document.getElementById('loading-text')
};

function setActiveTab(tabName) {
    const isVerifyTab = tabName === 'verify';

    if (elements.drawTabBtn) {
        elements.drawTabBtn.classList.toggle('is-active', !isVerifyTab);
        elements.drawTabBtn.setAttribute('aria-selected', String(!isVerifyTab));
    }
    if (elements.verifyTabBtn) {
        elements.verifyTabBtn.classList.toggle('is-active', isVerifyTab);
        elements.verifyTabBtn.setAttribute('aria-selected', String(isVerifyTab));
    }

    if (elements.drawPanel) {
        elements.drawPanel.classList.toggle('is-active', !isVerifyTab);
    }
    if (elements.verifyPanel) {
        elements.verifyPanel.classList.toggle('is-active', isVerifyTab);
    }
}

// Fixed spin timing (normal speed)
const SPIN_CONFIG = { rotations: 5, duration: 4000 };

// Get user ID from Telegram
function getUserId() {
    // Note: For production, you should validate the Telegram init data signature
    // to ensure authenticity and prevent tampering. See:
    // https://core.telegram.org/bots/webapps#validating-data-received-via-the-mini-app
    return tg?.initDataUnsafe?.user?.id || null;
}

// Sync credits and user status with server
async function syncCredits() {
    const userId = getUserId();
    if (!userId) {
        console.warn('No user ID available, using localStorage fallback');
        const savedCredits = localStorage.getItem('namepicker_credits');
        state.credits = savedCredits ? parseInt(savedCredits, 10) : 1;
        state.isPro = false;
        updateCreditsDisplay();
        return;
    }

    try {
        const response = await fetch(`/api/get-user-status?userId=${userId}`);
        const data = await response.json();
        
        if (data.success) {
            state.credits = data.credits;
            state.isPro = data.isPro;
            state.canWatchAd = data.canWatchAd;
            state.adBlockReason = data.adBlockReason;
            state.adMetadata = data.adMetadata;
            updateCreditsDisplay();
            updateAdButton();
            console.log(`User status synced from server:`, {
                credits: state.credits,
                isPro: state.isPro,
                canWatchAd: state.canWatchAd
            });
        } else {
            throw new Error(data.message || 'Failed to sync user status');
        }
    } catch (error) {
        console.error('Error syncing user status:', error);
        // Fallback to localStorage if server fails
        const savedCredits = localStorage.getItem('namepicker_credits');
        state.credits = savedCredits ? parseInt(savedCredits, 10) : 1;
        state.isPro = false;
        updateCreditsDisplay();
    }
}

// Initialize credits from server
async function initCredits() {
    await syncCredits();
}

// Update credits display
function updateCreditsDisplay() {
    if (state.isPro) {
        elements.creditsCount.textContent = '∞ (Pro)';
    } else {
        elements.creditsCount.textContent = state.credits;
    }
    updateSpinButton();
}

// Update spin button state
function updateSpinButton() {
    const hasEnoughCredits = state.isPro || state.credits >= 1;
    const hasEnoughNames = state.names.length >= 2;
    const actionText = state.provablyFairEnabled ? 'Start Fair Draw' : 'Spin the Wheel';
    let pickCount = parseInt(elements.pickCount.value, 10);
    
    // Validate and clamp pick count
    if (isNaN(pickCount) || pickCount < 1) {
        pickCount = 1;
    }
    if (pickCount > MAX_WINNERS) {
        pickCount = MAX_WINNERS;
    }
    
    const canPick = state.names.length >= pickCount;
    
    elements.spinBtn.disabled = !hasEnoughCredits || !hasEnoughNames || !canPick || state.isSpinning;
    
    if (!hasEnoughNames) {
        elements.spinBtn.innerHTML = 'Enter at least 2 names';
    } else if (!canPick) {
        elements.spinBtn.innerHTML = `Not enough names (need ${pickCount}, have ${state.names.length})`;
    } else if (!hasEnoughCredits) {
        elements.spinBtn.innerHTML = `Need Credits to ${state.provablyFairEnabled ? 'Start Draw' : 'Spin'}`;
    } else if (state.isPro) {
        elements.spinBtn.innerHTML = `${actionText} (Pro - Free)`;
    } else {
        elements.spinBtn.innerHTML = `${actionText} (1 Credit)`;
    }
    
    // Update max attribute on pick count input
    elements.pickCount.max = Math.min(state.names.length, MAX_WINNERS);
}

// Save credits to server
async function saveCredits() {
    const userId = getUserId();
    if (!userId) {
        console.warn('No user ID available, saving to localStorage');
        localStorage.setItem('namepicker_credits', state.credits.toString());
        updateCreditsDisplay();
        return;
    }

    try {
        const response = await fetch('/api/update-credits', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                userId,
                amount: state.credits,
                operation: 'set'
            })
        });

        const data = await response.json();
        
        if (data.success) {
            console.log(`Credits saved to server: ${data.credits}`);
            updateCreditsDisplay();
        } else {
            throw new Error(data.message || 'Failed to save credits');
        }
    } catch (error) {
        console.error('Error saving credits:', error);
        // Fallback to localStorage if server fails
        localStorage.setItem('namepicker_credits', state.credits.toString());
        updateCreditsDisplay();
    }
}

// Update ad metadata locally (for UI state only)
async function updateAdMetadataLocal(updates) {
    // Merge with current metadata
    state.adMetadata = { ...state.adMetadata, ...updates };
    
    // Update UI based on new metadata
    updateAdButton();
}

// Update ad button state based on eligibility
function updateAdButton() {
    if (!elements.watchAdBtn) return;
    
    if (state.isPro) {
        // Pro users have unlimited credits - hide entire earn credits section
        if (elements.earnCreditsSection) {
            elements.earnCreditsSection.style.display = 'none';
        }
    } else {
        // Show the earn credits section for non-Pro users
        if (elements.earnCreditsSection) {
            elements.earnCreditsSection.style.display = 'block';
        }
        
        if (!state.canWatchAd) {
            elements.watchAdBtn.disabled = true;
            elements.watchAdBtn.innerHTML = state.adBlockReason || 'Watch Ad (Not Available)';
        } else {
            elements.watchAdBtn.disabled = false;
            elements.watchAdBtn.innerHTML = 'Ad +10';
        }
    }
}

// Add credits
async function addCredits(amount) {
    state.credits += amount;
    await saveCredits();
    tg.showAlert(`You earned ${amount} credit${amount > 1 ? 's' : ''}!`);
}

// Deduct credits and track usage
async function deductCredits(amount) {
    // Pro users don't need credits
    if (state.isPro) {
        return true;
    }
    
    if (state.credits >= amount) {
        const userId = getUserId();
        
        if (!userId) {
            // Fallback to localStorage
            state.credits -= amount;
            localStorage.setItem('namepicker_credits', state.credits.toString());
            updateCreditsDisplay();
            return true;
        }
        
        try {
            // Deduct credits and update ad metadata on server
            const response = await fetch('/api/update-credits', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId,
                    amount,
                    operation: 'subtract',
                    adMetadata: { creditsUsed: state.adMetadata.creditsUsed + amount }
                })
            });

            const data = await response.json();
            
            if (data.success) {
                state.credits = data.credits;
                state.adMetadata.creditsUsed += amount;
                updateCreditsDisplay();
                
                // Update ad button eligibility after using credits
                await syncCredits();
                
                return true;
            } else {
                throw new Error(data.message || 'Failed to deduct credits');
            }
        } catch (error) {
            console.error('Error deducting credits:', error);
            // Fallback to local update
            state.credits -= amount;
            localStorage.setItem('namepicker_credits', state.credits.toString());
            updateCreditsDisplay();
            return true;
        }
    }
    return false;
}

function bytesToHex(bytes) {
    return `0x${Array.from(bytes).map(byte => byte.toString(16).padStart(2, '0')).join('')}`;
}

// Track private daily analytics events in Upstash
async function trackAnalyticsEvent(event, amount = 1) {
    const userId = getUserId();
    if (!userId) {
        return;
    }

    try {
        await fetch('/api/track-analytics', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                userId,
                event,
                amount
            })
        });
    } catch (error) {
        console.warn('Analytics tracking failed:', error);
    }
}

function hexToBytes(hexValue) {
    const normalized = (hexValue || '').replace(/^0x/, '').toLowerCase();
    if (normalized.length % 2 !== 0) {
        throw new Error('Invalid hex length');
    }

    const bytes = new Uint8Array(normalized.length / 2);
    for (let i = 0; i < normalized.length; i += 2) {
        bytes[i / 2] = parseInt(normalized.slice(i, i + 2), 16);
    }
    return bytes;
}

function concatBytes(...chunks) {
    const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const result = new Uint8Array(total);
    let offset = 0;
    chunks.forEach(chunk => {
        result.set(chunk, offset);
        offset += chunk.length;
    });
    return result;
}

async function sha256HexFromText(text) {
    const data = new TextEncoder().encode(String(text));
    const digest = await crypto.subtle.digest('SHA-256', data);
    return bytesToHex(new Uint8Array(digest));
}

async function sha256HexFromBytes(bytes) {
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return bytesToHex(new Uint8Array(digest));
}

function normalizeEntrantsForProvablyFair(names, caseFold = false) {
    const normalizedNames = (Array.isArray(names) ? names : [])
        .map(name => String(name || '').trim())
        .filter(name => name.length > 0)
        .map(name => (caseFold ? name.toLocaleLowerCase('en-US') : name))
        .slice(0, MAX_NAMES);

    return {
        entrants: normalizedNames,
        canonicalEntrantsText: normalizedNames.join('\n')
    };
}

function parseSeparatedValues(text, delimiter) {
    const rows = [];
    let row = [];
    let cell = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];
        const next = text[i + 1];

        if (char === '"') {
            if (inQuotes && next === '"') {
                cell += '"';
                i += 1;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }

        if (!inQuotes && (char === delimiter || char === '\n')) {
            row.push(cell.trim());
            cell = '';

            if (char === '\n') {
                rows.push(row);
                row = [];
            }
            continue;
        }

        if (char !== '\r') {
            cell += char;
        }
    }

    row.push(cell.trim());
    rows.push(row);

    const cleanedRows = rows
        .map(r => r.map(cell => cell.trim()))
        .filter(r => r.some(cell => cell.length > 0));

    if (cleanedRows.length === 0) {
        return [];
    }

    const header = cleanedRows[0].map(cell => cell.toLowerCase());
    const explicitNameColumn = header.findIndex(cell => cell === 'name' || cell === 'full name');
    if (explicitNameColumn >= 0 && cleanedRows.length > 1) {
        return cleanedRows
            .slice(1)
            .map(r => (r[explicitNameColumn] || '').trim())
            .filter(value => value.length > 0);
    }

    const hasMultipleColumns = cleanedRows.some(r => r.length > 1);
    if (hasMultipleColumns && cleanedRows.length > 1) {
        const firstColumn = cleanedRows
            .map(r => (r[0] || '').trim())
            .filter(value => value.length > 0);
        if (firstColumn.length > 1 && firstColumn[0].toLowerCase() === 'name') {
            return firstColumn.slice(1);
        }
        return firstColumn;
    }

    return cleanedRows.flat().filter(value => value.length > 0);
}

function extractNamesFromJson(value) {
    if (typeof value === 'string') {
        return [value.trim()];
    }

    if (Array.isArray(value)) {
        return value.flatMap(item => extractNamesFromJson(item));
    }

    if (value && typeof value === 'object') {
        if (typeof value.name === 'string') {
            return [value.name.trim()];
        }
        return Object.values(value).flatMap(item => extractNamesFromJson(item));
    }

    return [];
}

function parseImportedNames(text, fileName = '') {
    const cleanText = String(text || '').replace(/\r\n?/g, '\n').trim();
    if (!cleanText) {
        return [];
    }

    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith('.json')) {
        try {
            const parsed = JSON.parse(cleanText);
            return extractNamesFromJson(parsed).filter(name => name.length > 0);
        } catch (error) {
            throw new Error('Invalid JSON file');
        }
    }

    if (lowerName.endsWith('.tsv')) {
        return parseSeparatedValues(cleanText, '\t');
    }

    if (lowerName.endsWith('.csv')) {
        return parseSeparatedValues(cleanText, ',');
    }

    if (cleanText.includes('\n')) {
        return cleanText
            .split('\n')
            .map(name => name.trim())
            .filter(name => name.length > 0);
    }

    if (cleanText.includes('\t')) {
        return parseSeparatedValues(cleanText, '\t');
    }

    if (cleanText.includes(';')) {
        return parseSeparatedValues(cleanText, ';');
    }

    if (cleanText.includes(',')) {
        return parseSeparatedValues(cleanText, ',');
    }

    return [cleanText];
}

function appendImportedNames(importedNames) {
    const freeSlots = MAX_NAMES - state.names.length;
    const namesToAdd = importedNames.slice(0, Math.max(freeSlots, 0));
    state.names = [...state.names, ...namesToAdd];
    elements.namesInput.value = state.names.join('\n');
    elements.nameCount.textContent = state.names.length;
    drawWheel();
    updateSpinButton();

    return {
        added: namesToAdd.length,
        ignored: importedNames.length - namesToAdd.length
    };
}

function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function formatDateTime(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
}

function normalizeReceiptCode(value) {
    const compact = String(value || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');

    if (!compact.startsWith('RN') || compact.length < 10) {
        return '';
    }

    const body = compact.slice(2, 10);
    return `RN-${body.slice(0, 4)}-${body.slice(4, 8)}`;
}

function buildVerifyUrl(receiptCode) {
    if (!receiptCode) {
        return null;
    }

    return `https://t.me/KoyRandomNamePickerBot/picker?startapp=verify_${encodeURIComponent(receiptCode)}`;
}

function buildVerificationBadge(verification) {
    if (!verification || verification.overallVerified === null || verification.overallVerified === undefined) {
        return 'Blockchain verification unavailable';
    }

    return verification.overallVerified
        ? 'Verified on blockchain'
        : 'Blockchain verification mismatch';
}

function renderReceiptLookupResult(data) {
    if (!elements.verifyResult) {
        return;
    }

    if (!data) {
        elements.verifyResult.style.display = 'none';
        elements.verifyResult.innerHTML = '';
        return;
    }

    const winners = Array.isArray(data.winners) ? data.winners : [];
    const winnerPills = winners.length > 0
        ? `<div class="verify-result-list">${winners.map((winner) => `<span class="verify-pill">${escapeHtml(winner)}</span>`).join('')}</div>`
        : '<div class="verify-result-list"><span class="verify-pill">No winners yet</span></div>';

    const links = [
        data.commitTxUrl ? `<a href="${escapeHtml(data.commitTxUrl)}" target="_blank" rel="noopener noreferrer">Blockchain record #1</a>` : null,
        data.finalTxUrl ? `<a href="${escapeHtml(data.finalTxUrl)}" target="_blank" rel="noopener noreferrer">Blockchain record #2</a>` : null
    ].filter(Boolean).join('');

    const entrantsDisclosure = Array.isArray(data.entrants) && data.entrants.length > 0
        ? `<details class="lookup-entrants"><summary>Participant list (${data.entrants.length})</summary><pre>${escapeHtml(data.entrants.join('\n'))}</pre></details>`
        : (data.publicEntrants
            ? ''
            : '<div class="lookup-entrants">Participant list is private for this draw.</div>');

    elements.verifyResult.innerHTML = `
        <strong>${data.status === 'finalized' ? 'Receipt verified' : 'Receipt found (pending)'}</strong><br>
        Receipt: <code>${escapeHtml(data.receiptCode || '-')}</code><br>
        Created: ${escapeHtml(formatDateTime(data.createdAt))}<br>
        Finalized: ${escapeHtml(formatDateTime(data.finalizedAt))}<br>
        Participants: ${escapeHtml(data.participantsCount)}<br>
        Winners requested: ${escapeHtml(data.winnersRequested)}<br>
        ${buildVerificationBadge(data.verification)}<br>
        ${escapeHtml(data.message || '')}
        ${winnerPills}
        ${entrantsDisclosure}
        ${links ? `<div class="verify-result-links">${links}</div>` : ''}
    `;
    elements.verifyResult.style.display = 'block';
}

function getReceiptCodeFromLaunchContext() {
    const fromQuery = normalizeReceiptCode(new URLSearchParams(window.location.search).get('verify'));
    if (fromQuery) {
        return fromQuery;
    }

    const startParamRaw = tg?.initDataUnsafe?.start_param;
    const startParam = String(startParamRaw || '').trim();
    if (!startParam) {
        return '';
    }

    if (startParam.startsWith('verify_')) {
        return normalizeReceiptCode(startParam.slice('verify_'.length));
    }
    if (startParam.startsWith('verify-')) {
        return normalizeReceiptCode(startParam.slice('verify-'.length));
    }

    return normalizeReceiptCode(startParam);
}

function showLoadingOverlay(text) {
    if (!elements.loadingOverlay || !elements.loadingText) return;
    elements.loadingText.textContent = text;
    elements.loadingOverlay.style.display = 'flex';
}

function hideLoadingOverlay() {
    if (!elements.loadingOverlay) return;
    elements.loadingOverlay.style.display = 'none';
}

function setDrawInteractionLocked(locked) {
    if (elements.namesInput) {
        elements.namesInput.readOnly = locked;
    }
    if (elements.importFileBtn) {
        elements.importFileBtn.disabled = locked;
    }
    if (elements.importClipboardBtn) {
        elements.importClipboardBtn.disabled = locked;
    }
    if (elements.pickCount) {
        elements.pickCount.disabled = locked;
    }
    if (elements.provablyFairToggle) {
        elements.provablyFairToggle.disabled = locked;
    }
    if (elements.publicEntrantsToggle) {
        elements.publicEntrantsToggle.disabled = locked;
    }
}

function renderProvablyFairStatus(data, isFinal = false) {
    if (!elements.provablyFairStatus) return;

    if (!data) {
        elements.provablyFairStatus.style.display = 'none';
        elements.provablyFairStatus.innerHTML = '';
        return;
    }

    const statusTitle = isFinal ? 'Draw complete' : 'Participants locked';
    const receiptCode = normalizeReceiptCode(data.receiptCode);
    const participantCount = typeof data.participantsCount === 'number'
        ? data.participantsCount
        : (Array.isArray(data.entrants) ? data.entrants.length : state.names.length);
    const chainText = `${data.chain || '-'}${data.chainId ? ` (chainId ${data.chainId})` : ''}`;
    const blockLine = data.targetBlock
        ? `Public randomness block: #${escapeHtml(data.targetBlock)}`
        : 'Public randomness block: -';
    const remainingLine = typeof data.blocksRemaining === 'number'
        ? `Blocks remaining: ${data.blocksRemaining}`
        : '';

    const links = [
        data.commitTxUrl ? `<a href="${escapeHtml(data.commitTxUrl)}" target="_blank" rel="noopener noreferrer">Blockchain record #1</a>` : null,
        data.finalTxUrl ? `<a href="${escapeHtml(data.finalTxUrl)}" target="_blank" rel="noopener noreferrer">Blockchain record #2</a>` : null
    ].filter(Boolean).join(' | ');

    elements.provablyFairStatus.innerHTML = `
        <strong>${statusTitle}</strong><br>
        ${receiptCode ? `Fairness receipt: <code>${escapeHtml(receiptCode)}</code><br>` : ''}
        Participants: ${escapeHtml(participantCount)}<br>
        Chain: ${escapeHtml(chainText)}<br>
        ${blockLine}<br>
        ${remainingLine ? `<br>${escapeHtml(remainingLine)}` : ''}
        ${links ? `<br>${links}` : ''}
    `;
    elements.provablyFairStatus.style.display = 'block';
}

function renderProofSection() {
    const proof = state.currentProof;
    if (!elements.proofSection || !elements.proofSummary || !elements.proofJson) return;

    if (!proof) {
        elements.proofSection.style.display = 'none';
        elements.proofSummary.innerHTML = '';
        elements.proofJson.style.display = 'none';
        elements.proofJson.textContent = '';
        if (elements.toggleProofBtn) elements.toggleProofBtn.textContent = 'Show Technical Details';
        return;
    }

    const receiptCode = normalizeReceiptCode(proof.receiptCode);
    const verifyUrl = buildVerifyUrl(receiptCode);
    const participantCount = Array.isArray(proof.entrants) ? proof.entrants.length : 0;
    const commitLink = proof.commitTxUrl
        ? `<a href="${escapeHtml(proof.commitTxUrl)}" target="_blank" rel="noopener noreferrer">Blockchain record #1</a>`
        : escapeHtml(proof.commitTx || '-');
    const finalLink = proof.finalTxUrl
        ? `<a href="${escapeHtml(proof.finalTxUrl)}" target="_blank" rel="noopener noreferrer">Blockchain record #2</a>`
        : escapeHtml(proof.finalTx || '-');

    elements.proofSummary.innerHTML = `
        Fairness receipt: <code>${escapeHtml(receiptCode || '-')}</code><br>
        Created: ${escapeHtml(formatDateTime(proof.createdAt))}<br>
        Finalized: ${escapeHtml(formatDateTime(proof.finalizedAt))}<br>
        Participants: ${escapeHtml(participantCount)}<br>
        Winners selected: ${escapeHtml(Array.isArray(proof.winners) ? proof.winners.length : 0)}<br>
        Public lookup participant visibility: ${proof.publicEntrants ? 'Visible' : 'Hidden'}<br>
        Public randomness block: #${escapeHtml(proof.targetBlock)}<br>
        ${verifyUrl ? `Verify link: <a href="${escapeHtml(verifyUrl)}" target="_blank" rel="noopener noreferrer">Open receipt lookup</a><br>` : ''}
        ${commitLink} | ${finalLink}
    `;

    elements.proofJson.textContent = JSON.stringify(proof, null, 2);
    elements.proofJson.style.display = 'none';
    if (elements.toggleProofBtn) {
        elements.toggleProofBtn.textContent = 'Show Technical Details';
    }
    elements.proofSection.style.display = 'block';
}

function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

async function deterministicWinnersFromSeed(seedHex, entrants, pickCount) {
    const indices = Array.from({ length: entrants.length }, (_, idx) => idx);
    let counter = 0;

    for (let i = indices.length - 1; i > 0; i -= 1) {
        const range = i + 1;
        const maxAccept = Math.floor(0x100000000 / range) * range - 1;

        let value;
        do {
            const digestHex = await sha256HexFromText(`${seedHex}:${counter}`);
            const digestBytes = hexToBytes(digestHex);
            value = (
                (digestBytes[0] * 16777216) +
                (digestBytes[1] * 65536) +
                (digestBytes[2] * 256) +
                digestBytes[3]
            ) >>> 0;
            counter += 1;
        } while (value > maxAccept);

        const j = value % range;
        [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    const winnerIndices = indices.slice(0, pickCount);
    const winners = winnerIndices.map(idx => entrants[idx]);
    return { winners, winnerIndices };
}

async function verifyProofLocally(proof) {
    if (!proof || !Array.isArray(proof.entrants) || !Array.isArray(proof.winners)) {
        return { ok: false, reason: 'Proof is missing required fields' };
    }

    const caseFold = Boolean(proof?.canonicalization?.caseFold);
    const normalized = normalizeEntrantsForProvablyFair(proof.entrants, caseFold);
    const entrantsHash = await sha256HexFromText(normalized.canonicalEntrantsText);
    if (entrantsHash.toLowerCase() !== String(proof.entrantsHash || '').toLowerCase()) {
        return { ok: false, reason: 'Entrants hash mismatch' };
    }

    const commitmentBytes = concatBytes(
        hexToBytes(proof.secret),
        hexToBytes(proof.entrantsHash),
        new TextEncoder().encode(String(proof.drawId))
    );
    const commitment = await sha256HexFromBytes(commitmentBytes);
    if (commitment.toLowerCase() !== String(proof.commitment || '').toLowerCase()) {
        return { ok: false, reason: 'Commitment mismatch' };
    }

    const seedBytes = concatBytes(
        hexToBytes(proof.secret),
        hexToBytes(proof.entrantsHash),
        hexToBytes(proof.blockHash),
        new TextEncoder().encode(String(proof.drawId))
    );
    const seed = await sha256HexFromBytes(seedBytes);
    const seedHash = await sha256HexFromBytes(hexToBytes(seed));
    if (proof.seedHash && seedHash.toLowerCase() !== String(proof.seedHash).toLowerCase()) {
        return { ok: false, reason: 'Seed hash mismatch' };
    }

    const { winners, winnerIndices } = await deterministicWinnersFromSeed(seed, normalized.entrants, proof.winners.length);
    const winnersHash = await sha256HexFromText(winners.join('\n'));

    const winnersMatch = winners.length === proof.winners.length
        && winners.every((winner, idx) => winner === proof.winners[idx]);

    if (!winnersMatch) {
        return { ok: false, reason: 'Winners list mismatch' };
    }

    if (proof.winnersHash && winnersHash.toLowerCase() !== String(proof.winnersHash).toLowerCase()) {
        return { ok: false, reason: 'Winners hash mismatch' };
    }

    return {
        ok: true,
        winnerIndices,
        winners
    };
}

// Draw the wheel
function drawWheel() {
    const canvas = elements.wheelCanvas;
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 10;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (state.names.length === 0) {
        // Draw empty wheel placeholder
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.fillStyle = '#f0f0f0';
        ctx.fill();
        ctx.strokeStyle = '#ddd';
        ctx.lineWidth = 3;
        ctx.stroke();
        
        ctx.fillStyle = '#999';
        ctx.font = '16px -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Enter names above', centerX, centerY);
        return;
    }
    
    const sliceAngle = (2 * Math.PI) / state.names.length;
    
    // Save context and apply rotation
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(state.wheelRotation);
    ctx.translate(-centerX, -centerY);
    
    // Draw slices
    state.names.forEach((name, index) => {
        const startAngle = index * sliceAngle - Math.PI / 2;
        const endAngle = startAngle + sliceAngle;
        
        // Draw slice
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = WHEEL_COLORS[index % WHEEL_COLORS.length];
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Draw name
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(startAngle + sliceAngle / 2);
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, sans-serif';
        
        // Truncate name if too long
        let displayName = name;
        const maxLen = state.names.length > 10 ? 8 : 12;
        if (displayName.length > maxLen) {
            displayName = displayName.substring(0, maxLen - 1) + '…';
        }
        
        ctx.fillText(displayName, radius - 15, 0);
        ctx.restore();
    });
    
    ctx.restore();
    
    // Draw center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, 20, 0, 2 * Math.PI);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 3;
    ctx.stroke();
}

// Get the index of the slice that the pointer is currently pointing to
function getSliceAtPointer() {
    const sliceAngle = (2 * Math.PI) / state.names.length;
    // The pointer is at the top (-π/2 in canvas coordinates)
    // After rotation, a slice that was at angle θ is now at θ + rotation
    // We want to find which slice's range includes -π/2
    // That means: startAngle + rotation ≤ -π/2 < endAngle + rotation
    // Or: startAngle ≤ -π/2 - rotation < endAngle
    
    // Normalize the effective angle we're looking for
    let effectiveAngle = (-Math.PI / 2 - state.wheelRotation) % (2 * Math.PI);
    if (effectiveAngle < 0) effectiveAngle += 2 * Math.PI;
    // Adjust to match slice coordinate system (slices start at -π/2)
    effectiveAngle = (effectiveAngle + Math.PI / 2) % (2 * Math.PI);
    
    const index = Math.floor(effectiveAngle / sliceAngle);
    return Math.min(index, state.names.length - 1);
}

// Spin the wheel animation - spins randomly and returns the winning index
function spinWheel() {
    return new Promise((resolve) => {
        const totalRotations = SPIN_CONFIG.rotations + Math.random() * 2; // Add some randomness
        const sliceAngle = (2 * Math.PI) / state.names.length;
        
        // Pick a random slice to land on (this will be the winning index)
        const targetIndex = Math.floor(Math.random() * state.names.length);
        // Add small random offset within the slice for more natural feel
        const offsetWithinSlice = (Math.random() * 0.6 + 0.2) * sliceAngle; // 20-80% into the slice
        
        // Calculate target rotation
        // The pointer is at the top (-π/2). After rotation R, slice i's start is at:
        // (i * sliceAngle - π/2) + R
        // For the pointer to be in slice i, we need:
        // -π/2 to be between slice i's start and end after rotation
        // i.e., (i * sliceAngle - π/2 + R) ≤ -π/2 < ((i+1) * sliceAngle - π/2 + R)
        // Simplifying: i * sliceAngle + R ≤ 0 < (i+1) * sliceAngle + R
        // So: R = -i * sliceAngle - offset (where 0 < offset < sliceAngle)
        // For positive rotation: R = 2πn - i * sliceAngle - offset
        
        const targetRotation = totalRotations * 2 * Math.PI - targetIndex * sliceAngle - offsetWithinSlice;
        
        const startRotation = state.wheelRotation;
        // Ensure we always spin forward by at least the minimum rotations
        let totalDelta = targetRotation - startRotation;
        const minRotation = SPIN_CONFIG.rotations * 2 * Math.PI;
        if (totalDelta < minRotation) {
            // Calculate how many full rotations to add
            const rotationsToAdd = Math.ceil((minRotation - totalDelta) / (2 * Math.PI));
            totalDelta += rotationsToAdd * 2 * Math.PI;
        }
        
        const duration = SPIN_CONFIG.duration + Math.random() * 500; // Add some randomness
        const startTime = performance.now();
        
        function animate(currentTime) {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Normal easing
            const eased = 1 - Math.pow(1 - progress, 3);
            
            state.wheelRotation = startRotation + totalDelta * eased;
            drawWheel();
            
            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                // Normalize rotation to 0-2π range
                state.wheelRotation = state.wheelRotation % (2 * Math.PI);
                if (state.wheelRotation < 0) state.wheelRotation += 2 * Math.PI;
                
                // Return the index the pointer landed on
                resolve(getSliceAtPointer());
            }
        }
        
        requestAnimationFrame(animate);
    });
}

function spinWheelToIndex(targetIndex) {
    return new Promise((resolve) => {
        const totalRotations = SPIN_CONFIG.rotations + Math.random() * 2;
        const sliceAngle = (2 * Math.PI) / state.names.length;
        const safeIndex = Math.max(0, Math.min(targetIndex, state.names.length - 1));
        const offsetWithinSlice = (Math.random() * 0.6 + 0.2) * sliceAngle;
        const targetRotation = totalRotations * 2 * Math.PI - safeIndex * sliceAngle - offsetWithinSlice;

        const startRotation = state.wheelRotation;
        let totalDelta = targetRotation - startRotation;
        const minRotation = SPIN_CONFIG.rotations * 2 * Math.PI;
        if (totalDelta < minRotation) {
            const rotationsToAdd = Math.ceil((minRotation - totalDelta) / (2 * Math.PI));
            totalDelta += rotationsToAdd * 2 * Math.PI;
        }

        const duration = SPIN_CONFIG.duration + Math.random() * 500;
        const startTime = performance.now();

        function animate(currentTime) {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const eased = 1 - Math.pow(1 - progress, 3);

            state.wheelRotation = startRotation + totalDelta * eased;
            drawWheel();

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                state.wheelRotation = state.wheelRotation % (2 * Math.PI);
                if (state.wheelRotation < 0) state.wheelRotation += 2 * Math.PI;
                resolve(getSliceAtPointer());
            }
        }

        requestAnimationFrame(animate);
    });
}

// Pick random names using Fisher-Yates shuffle for proper randomization
function pickRandomNames(count) {
    const shuffled = [...state.names];
    // Fisher-Yates shuffle algorithm
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, count);
}

// Handle spin button click
async function handleSpin() {
    if (state.provablyFairEnabled) {
        await handleProvablyFairSpin();
        return;
    }

    await handleStandardSpin();
}

async function handleStandardSpin() {
    if (state.isSpinning) return;
    
    let pickCount = parseInt(elements.pickCount.value, 10);
    
    // Validate pick count
    if (isNaN(pickCount) || pickCount < 1) {
        pickCount = 1;
        elements.pickCount.value = 1;
    }
    if (pickCount > MAX_WINNERS) {
        pickCount = MAX_WINNERS;
        elements.pickCount.value = MAX_WINNERS;
    }
    
    // Check if we have enough names
    if (state.names.length < 2) {
        tg.showAlert('Please enter at least 2 names!');
        return;
    }
    
    if (state.names.length < pickCount) {
        tg.showAlert(`Not enough names! You have ${state.names.length} names but want to pick ${pickCount}. Please reduce the number of winners or add more names.`);
        return;
    }
    
    // Check credits (Pro users bypass this)
    if (!state.isPro && state.credits < 1) {
        tg.showAlert('Not enough credits! Watch an ad or purchase credits.');
        return;
    }
    
    // Deduct credit (Pro users don't lose credits)
    const deducted = await deductCredits(1);
    if (!deducted) {
        tg.showAlert('Not enough credits!');
        return;
    }

    state.currentProof = null;
    renderProofSection();
    renderProvablyFairStatus(null);
    
    state.isSpinning = true;
    setDrawInteractionLocked(true);
    elements.spinBtn.disabled = true;
    elements.resultsSection.style.display = 'none';

    try {
        if (pickCount === 1) {
            // Single winner: spin the wheel and the arrow determines the winner
            // Show the pointer
            if (elements.wheelPointer) elements.wheelPointer.style.display = 'block';

            const winnerIndex = await spinWheel();
            state.selectedNames = [state.names[winnerIndex]];
        } else {
            // Multiple winners: hide the arrow since we can't point to multiple winners
            // Pick randomly without specific wheel animation
            if (elements.wheelPointer) elements.wheelPointer.style.display = 'none';

            state.selectedNames = pickRandomNames(pickCount);

            // Still spin the wheel for visual effect, but the result is random
            await spinWheel();
        }

        // Show results
        showResults();
        trackAnalyticsEvent('draw_completed');
    } finally {
        state.isSpinning = false;
        setDrawInteractionLocked(false);
        updateSpinButton();
    }
}

async function pollProvablyFairDraw(drawId, targetBlock) {
    for (let attempt = 0; attempt < 180; attempt += 1) {
        const response = await fetch(`/api/provably-fair-status?drawId=${encodeURIComponent(drawId)}`, {
            method: 'GET'
        });
        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to fetch draw status');
        }

        if (data.status === 'finalized') {
            return data;
        }

        renderProvablyFairStatus({
            receiptCode: data.receiptCode,
            chain: data.chain,
            chainId: data.chainId,
            targetBlock: data.targetBlock || targetBlock,
            blocksRemaining: data.blocksRemaining,
            participantsCount: state.names.length,
            commitTxUrl: data.commitTxUrl
        });

        showLoadingOverlay(`Waiting for public block #${targetBlock}... (${data.blocksRemaining} blocks remaining)`);
        await wait(3000);
    }

    throw new Error('Timed out while waiting for target block');
}

async function handleProvablyFairSpin() {
    if (state.isSpinning) return;

    let pickCount = parseInt(elements.pickCount.value, 10);
    if (isNaN(pickCount) || pickCount < 1) {
        pickCount = 1;
        elements.pickCount.value = 1;
    }
    if (pickCount > MAX_WINNERS) {
        pickCount = MAX_WINNERS;
        elements.pickCount.value = MAX_WINNERS;
    }

    if (state.names.length < 2) {
        tg.showAlert('Please enter at least 2 names!');
        return;
    }

    if (state.names.length < pickCount) {
        tg.showAlert(`Not enough names! You have ${state.names.length} names but want to pick ${pickCount}.`);
        return;
    }

    if (!state.isPro && state.credits < 1) {
        tg.showAlert('Not enough credits! Watch an ad or purchase credits.');
        return;
    }

    const deducted = await deductCredits(1);
    if (!deducted) {
        tg.showAlert('Not enough credits!');
        return;
    }

    state.isSpinning = true;
    setDrawInteractionLocked(true);
    state.currentProof = null;
    renderProofSection();
    elements.resultsSection.style.display = 'none';
    updateSpinButton();

    try {
        const normalized = normalizeEntrantsForProvablyFair(state.names, false);
        const expectedEntrantsHash = await sha256HexFromText(normalized.canonicalEntrantsText);

        showLoadingOverlay('Locking participants and writing receipt to blockchain...');

        const startResponse = await fetch('/api/provably-fair-start', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userId: getUserId(),
                entrants: normalized.entrants,
                pickCount,
                caseFold: false,
                publicEntrants: state.publicEntrantsEnabled
            })
        });

        const startData = await startResponse.json();
        if (!startResponse.ok || !startData.success) {
            throw new Error(startData.message || 'Failed to start provably fair draw');
        }

        if (startData.entrantsHash.toLowerCase() !== expectedEntrantsHash.toLowerCase()) {
            throw new Error('Entrants hash mismatch while locking draw');
        }

        renderProvablyFairStatus(startData, false);

        const finalData = await pollProvablyFairDraw(startData.drawId, startData.targetBlock);

        state.selectedNames = finalData.winners;
        state.currentProof = finalData.proof;

        const receiptCode = normalizeReceiptCode(finalData?.proof?.receiptCode);
        if (receiptCode && elements.verifyReceiptInput) {
            elements.verifyReceiptInput.value = receiptCode;
        }

        if (pickCount === 1 && Array.isArray(finalData.winnerIndices) && finalData.winnerIndices.length > 0) {
            if (elements.wheelPointer) elements.wheelPointer.style.display = 'block';
            await spinWheelToIndex(finalData.winnerIndices[0]);
        } else {
            if (elements.wheelPointer) elements.wheelPointer.style.display = 'none';
            await spinWheel();
        }

        renderProvablyFairStatus(finalData.proof, true);
        showResults();
        trackAnalyticsEvent('draw_completed');
        trackAnalyticsEvent('draw_completed_provably_fair');
    } catch (error) {
        console.error('Provably fair draw error:', error);
        tg.showAlert(`Provably fair draw failed: ${error.message}`);
    } finally {
        hideLoadingOverlay();
        state.isSpinning = false;
        setDrawInteractionLocked(false);
        updateSpinButton();
    }
}

// Show results
function showResults() {
    elements.resultsList.innerHTML = '';
    
    state.selectedNames.forEach((name, index) => {
        const item = document.createElement('div');
        item.className = 'result-item';
        item.textContent = name;
        item.style.animationDelay = `${index * 0.1}s`;
        elements.resultsList.appendChild(item);
    });

    renderProofSection();
    
    elements.resultsSection.style.display = 'block';
    
    // Scroll to results
    elements.resultsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

// Copy results to clipboard
async function handleCopy() {
    const receiptCode = normalizeReceiptCode(state.currentProof?.receiptCode);
    const verifyUrl = buildVerifyUrl(receiptCode);
    let text = state.selectedNames.join('\n');

    if (receiptCode) {
        text += `\n\nFairness Receipt: ${receiptCode}`;
        if (verifyUrl) {
            text += `\nVerify: ${verifyUrl}`;
        }
    }

    text += '\n\n— https://t.me/KoyRandomNamePickerBot/picker';
    
    try {
        await navigator.clipboard.writeText(text);
        tg.showAlert('Results copied to clipboard!');
    } catch (error) {
        // Fallback for older browsers using deprecated execCommand API
        // This is intentionally kept for legacy browser support
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy'); // eslint-disable-line deprecation/deprecation
        document.body.removeChild(textarea);
        tg.showAlert('Results copied to clipboard!');
    }
}

// Share results
function handleShare() {
    const receiptCode = normalizeReceiptCode(state.currentProof?.receiptCode);
    const verifyUrl = buildVerifyUrl(receiptCode);
    let text = `Random Pick Winners:\n\n${state.selectedNames.map((n, i) => `${i + 1}. ${n}`).join('\n')}`;

    if (receiptCode) {
        text += `\n\nFairness Receipt: ${receiptCode}`;
        if (verifyUrl) {
            text += `\nVerify: ${verifyUrl}`;
        }
    }

    text += '\n\n— https://t.me/KoyRandomNamePickerBot/picker';
    
    if (navigator.share) {
        navigator.share({
            title: 'Random Name Picker Results',
            text: text
        }).catch(() => {
            // User cancelled or share failed
            handleCopy();
        });
    } else {
        handleCopy();
    }
}

// Pick Again - hide results, keep all names
function handlePickAgain() {
    state.selectedNames = [];
    state.currentProof = null;
    elements.resultsSection.style.display = 'none';
    renderProofSection();
    renderProvablyFairStatus(null);
    updateSpinButton();
}

// Remove winners from the list and pick again
function handleRemoveWinners() {
    // Remove selected names from the list
    const remainingNames = state.names.filter(name => !state.selectedNames.includes(name));
    
    if (remainingNames.length < 2) {
        tg.showAlert('Not enough names remaining! Add more names to continue picking.');
        return; // Don't proceed with state updates
    }
    
    // Update state and UI
    state.names = remainingNames;
    state.selectedNames = [];
    state.currentProof = null;
    elements.namesInput.value = state.names.join('\n');
    elements.nameCount.textContent = state.names.length;
    elements.resultsSection.style.display = 'none';
    renderProofSection();
    renderProvablyFairStatus(null);
    
    // Reset wheel rotation and redraw
    state.wheelRotation = 0;
    drawWheel();
    updateSpinButton();
}

// Start fresh - clear everything
function handleStartFresh() {
    state.names = [];
    state.selectedNames = [];
    state.currentProof = null;
    elements.namesInput.value = '';
    elements.nameCount.textContent = '0';
    elements.pickCount.value = '1';
    elements.resultsSection.style.display = 'none';
    renderProofSection();
    renderProvablyFairStatus(null);
    
    // Reset wheel
    state.wheelRotation = 0;
    drawWheel();
    updateSpinButton();
}

async function handleImportFromFile(file) {
    if (!file) return;

    try {
        const text = await file.text();
        const imported = parseImportedNames(text, file.name);
        if (imported.length === 0) {
            tg.showAlert('No names found in the selected file.');
            return;
        }

        const result = appendImportedNames(imported);
        tg.showAlert(`Imported ${result.added} name(s)${result.ignored > 0 ? `, ignored ${result.ignored} due to the 100-name limit` : ''}.`);
    } catch (error) {
        console.error('Import file error:', error);
        tg.showAlert(`Could not import file: ${error.message}`);
    } finally {
        if (elements.importFileInput) {
            elements.importFileInput.value = '';
        }
    }
}

async function handleImportFromClipboard() {
    try {
        const text = await navigator.clipboard.readText();
        const imported = parseImportedNames(text, 'clipboard.txt');
        if (imported.length === 0) {
            tg.showAlert('Clipboard does not contain names in a supported format.');
            return;
        }

        const result = appendImportedNames(imported);
        tg.showAlert(`Imported ${result.added} name(s) from clipboard${result.ignored > 0 ? `, ignored ${result.ignored}` : ''}.`);
    } catch (error) {
        console.error('Clipboard import error:', error);
        tg.showAlert('Clipboard import failed. Telegram may block clipboard access in this context.');
    }
}

function handleToggleProofJson() {
    if (!elements.proofJson || !elements.toggleProofBtn) return;
    const currentlyVisible = elements.proofJson.style.display !== 'none';
    elements.proofJson.style.display = currentlyVisible ? 'none' : 'block';
    elements.toggleProofBtn.textContent = currentlyVisible ? 'Show Technical Details' : 'Hide Technical Details';
}

async function handleCopyProofJson() {
    if (!state.currentProof) {
        tg.showAlert('No proof available yet.');
        return;
    }

    const text = JSON.stringify(state.currentProof, null, 2);
    try {
        await navigator.clipboard.writeText(text);
        tg.showAlert('Technical JSON copied to clipboard.');
    } catch (error) {
        console.error('Copy proof error:', error);
        tg.showAlert('Failed to copy technical JSON.');
    }
}

async function handleReplayDraw() {
    if (!state.currentProof) {
        tg.showAlert('No proof available yet.');
        return;
    }

    showLoadingOverlay('Replaying draw locally...');
    try {
        const verification = await verifyProofLocally(state.currentProof);
        if (!verification.ok) {
            tg.showAlert(`Replay failed: ${verification.reason}`);
            return;
        }

        tg.showAlert('Replay check passed. The same winners are reproduced from the receipt data.');
    } catch (error) {
        console.error('Replay draw error:', error);
        tg.showAlert(`Replay failed: ${error.message}`);
    } finally {
        hideLoadingOverlay();
    }
}

async function lookupDrawByReceipt(receiptCode, includeTechnical = false) {
    const params = new URLSearchParams({
        receiptCode,
        public: 'true',
        includeEntrants: 'true'
    });
    if (includeTechnical) {
        params.set('includeTechnical', 'true');
    }

    const response = await fetch(`/api/provably-fair-status?${params.toString()}`, {
        method: 'GET'
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
        throw new Error(data.message || 'Receipt lookup failed');
    }
    return data;
}

async function handleVerifyReceiptLookup() {
    if (!elements.verifyReceiptInput) {
        return;
    }

    const normalized = normalizeReceiptCode(elements.verifyReceiptInput.value);
    if (!normalized) {
        tg.showAlert('Please enter a valid Fairness Receipt (example: RN-ABCD-1234).');
        return;
    }

    elements.verifyReceiptInput.value = normalized;
    showLoadingOverlay('Checking fairness receipt...');
    try {
        const data = await lookupDrawByReceipt(normalized, false);
        renderReceiptLookupResult(data);

        const nextUrl = `${window.location.pathname}?verify=${encodeURIComponent(normalized)}`;
        window.history.replaceState({}, '', nextUrl);
    } catch (error) {
        console.error('Receipt lookup error:', error);
        renderReceiptLookupResult(null);
        tg.showAlert(`Receipt check failed: ${error.message}`);
    } finally {
        hideLoadingOverlay();
    }
}

// Send results to Telegram chat
async function handleSendToChat() {
    const userId = getUserId();
    
    if (!userId) {
        tg.showAlert('Cannot send to chat. Please open this app from Telegram.');
        return;
    }
    
    if (state.selectedNames.length === 0) {
        tg.showAlert('No results to send!');
        return;
    }
    
    // Show loading state
    elements.sendToChatBtn.disabled = true;
    elements.sendToChatBtn.innerHTML = 'Sending...';

    const receiptCode = normalizeReceiptCode(state.currentProof?.receiptCode);
    const verifyUrl = buildVerifyUrl(receiptCode);
    
    try {
        const response = await fetch('/api/send-results', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                chatId: userId,
                winners: state.selectedNames,
                totalNames: state.names.length,
                receiptCode: receiptCode || null,
                verifyUrl: verifyUrl || null
            })
        });
        
        const data = await response.json();
        
        if (response.ok && data.success) {
            tg.showAlert('Results sent to your chat! Check your conversation with the bot.');
        } else {
            throw new Error(data.message || 'Failed to send results');
        }
    } catch (error) {
        console.error('Error sending to chat:', error);
        tg.showAlert('Failed to send results to chat. Please try copying instead.');
    } finally {
        // Reset button state
        elements.sendToChatBtn.disabled = false;
        elements.sendToChatBtn.innerHTML = 'Send to Chat';
    }
}

// Load Adsgram SDK
function loadAdsgramSDK() {
    if (state.adsgramLoaded) return Promise.resolve();
    
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://sad.adsgram.ai/js/sad.min.js';
        script.onload = () => {
            state.adsgramLoaded = true;
            resolve();
        };
        script.onerror = () => reject(new Error('Failed to load Adsgram SDK'));
        document.head.appendChild(script);
    });
}

// Show Adsgram ad
async function showAdsgramAd() {
    // Check if user can watch ads
    if (!state.canWatchAd) {
        tg.showAlert(state.adBlockReason || 'You cannot watch ads at this time.');
        return;
    }
    
    // Pro users don't need ads
    if (state.isPro) {
        tg.showAlert('You have Pro status with unlimited credits. No need to watch ads!');
        return;
    }
    
    // Prevent multiple clicks
    if (state.isWatchingAd) {
        console.log('Ad already in progress, ignoring click');
        return;
    }
    
    // Set loading state
    state.isWatchingAd = true;
    elements.watchAdBtn.disabled = true;
    elements.watchAdBtn.classList.add('loading');
    const originalText = elements.watchAdBtn.innerHTML;
    elements.watchAdBtn.innerHTML = 'Loading Ad...';
    
    const resetButtonState = () => {
        state.isWatchingAd = false;
        elements.watchAdBtn.disabled = false;
        elements.watchAdBtn.classList.remove('loading');
        elements.watchAdBtn.innerHTML = originalText;
    };
    
    try {
        await loadAdsgramSDK();
        
        // Initialize Adsgram with your block ID
        // Replace 'YOUR_BLOCK_ID' with actual block ID from Adsgram dashboard
        const AdController = window.Adsgram.init({ blockId: "19725" });
        
        elements.watchAdBtn.innerHTML = 'Watching Ad...';
        
        AdController.show().then(async () => {
            // User watched the ad successfully
            resetButtonState();
            
            // Mark that user has watched an ad
            await updateAdMetadataOnServer({ hasWatchedAd: true });
            
            // Add credits
            await addCredits(10);
            await trackAnalyticsEvent('ad_watch_completed');
            
            // Resync to get updated ad eligibility
            await syncCredits();
        }).catch(async (result) => {
            resetButtonState();
            // User closed the ad or an error occurred
            if (result.error) {
                const compensation = await requestAdFailureCredit('adsgram_show_error');
                if (compensation.granted) {
                    tg.showAlert(`Ad failed to load. You received +${compensation.creditsAdded} credit.`);
                    await syncCredits();
                } else {
                    tg.showAlert(compensation.message || 'Failed to load ad. Please try again later.');
                }
            } else {
                tg.showAlert('Ad was closed before completion.');
            }
        });
    } catch (error) {
        console.error('Adsgram error:', error);
        resetButtonState();
        const compensation = await requestAdFailureCredit('adsgram_sdk_error');
        if (compensation.granted) {
            tg.showAlert(`Ad failed to start. You received +${compensation.creditsAdded} credit.`);
            await syncCredits();
        } else {
            tg.showAlert(compensation.message || 'Ad system is unavailable right now. Please try again later.');
        }
    }
}

// Ask backend for ad-failure compensation credit (+1 by default)
async function requestAdFailureCredit(reason) {
    const userId = getUserId();
    if (!userId) {
        return {
            granted: false,
            creditsAdded: 0,
            message: 'Ad failed and no Telegram user ID was available for compensation.'
        };
    }

    try {
        const response = await fetch('/api/add-ad-failure-credit', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                userId,
                reason
            })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to process ad-failure compensation');
        }

        if (typeof data.credits === 'number') {
            state.credits = data.credits;
            updateCreditsDisplay();
        }

        return data;
    } catch (error) {
        console.error('Error requesting ad-failure credit:', error);
        return {
            granted: false,
            creditsAdded: 0,
            message: 'Ad failed, but compensation could not be processed right now.'
        };
    }
}

// Update ad metadata on server
async function updateAdMetadataOnServer(updates) {
    const userId = getUserId();
    if (!userId) {
        console.warn('No user ID available, cannot update ad metadata on server');
        return;
    }

    try {
        const response = await fetch('/api/update-credits', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                userId,
                amount: 0,
                operation: 'add',
                adMetadata: updates
            })
        });

        const data = await response.json();
        
        if (data.success) {
            console.log('Ad metadata updated on server');
        } else {
            console.error('Failed to update ad metadata:', data.message);
        }
    } catch (error) {
        console.error('Error updating ad metadata on server:', error);
    }
}

// Helper: get current userId (for polling flow, with localStorage fallback)
function getCurrentUserId() {
    // Try to get from Telegram WebApp first (reuse existing function)
    const userId = getUserId();
    if (userId) return userId;
    
    // Fallback to localStorage
    try {
        const stored = localStorage.getItem('userId');
        if (stored) return stored;
    } catch (e) {
        console.warn('getCurrentUserId localStorage error', e);
    }
    return null;
}

// Update credits UI (for polling flow)
function updateCreditsUI({ credits, isPro }) {
    // Update state first
    state.isPro = isPro;
    state.credits = credits || state.credits;

    // Use the existing updateCreditsDisplay function for consistency
    updateCreditsDisplay();
    
    // Update pro badge visibility
    const proEl = document.getElementById('pro-status');
    if (proEl) {
        proEl.style.display = isPro ? 'inline-block' : 'none';
    }

    // Update buy buttons state
    const buyBtns = document.querySelectorAll('#buy-credits-btn, #unlock-pro-btn');
    buyBtns.forEach(b => {
        if (isPro) {
            b.disabled = true;
            b.classList.add('disabled');
            b.style.opacity = '0.6';
        } else {
            b.disabled = false;
            b.classList.remove('disabled');
            b.style.opacity = '';
        }
    });

    // Update ad button and earn credits section visibility
    updateAdButton();
    
    // Force update the spin button to reflect new Pro status
    updateSpinButton();
}

// Poll user status (for polling flow)
function pollUserStatus(userId, { interval = 2000, timeout = 30000, onProgress } = {}) {
    return new Promise((resolve, reject) => {
        const start = Date.now();
        let lastCredits = null;
        const tick = async () => {
            try {
                const url = `/api/get-credits?userId=${encodeURIComponent(userId)}`;
                const res = await fetch(url, { method: 'GET', cache: 'no-cache' });
                if (!res.ok) {
                    console.warn('pollUserStatus fetch failed', res.status);
                } else {
                    const json = await res.json();
                    if (json && json.success) {
                        const currentIsPro = json.isPro || json.is_pro || json.pro || false;
                        const currentCredits = typeof json.credits === 'number' ? json.credits : (json.credits ? Number(json.credits) : null);
                        if (typeof onProgress === 'function') onProgress({ credits: currentCredits, isPro: currentIsPro });
                        if (currentIsPro) {
                            resolve({ credits: currentCredits, isPro: true, raw: json });
                            return;
                        }
                        if (lastCredits !== null && currentCredits !== null && currentCredits !== lastCredits) {
                            resolve({ credits: currentCredits, isPro: false, raw: json });
                            return;
                        }
                        lastCredits = currentCredits;
                    }
                }
            } catch (err) {
                console.error('pollUserStatus error', err);
            }

            if (Date.now() - start >= timeout) {
                reject(new Error('poll timeout'));
            } else {
                setTimeout(tick, interval);
            }
        };
        tick();
    });
}

// Show processing overlay (for polling flow)
function showProcessingOverlay(text = 'Waiting for payment...') {
    let overlay = document.getElementById('payment-processing-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'payment-processing-overlay';
        overlay.style.position = 'fixed';
        overlay.style.left = '0';
        overlay.style.top = '0';
        overlay.style.right = '0';
        overlay.style.bottom = '0';
        overlay.style.background = 'rgba(0,0,0,0.6)';
        overlay.style.display = 'flex';
        overlay.style.alignItems = 'center';
        overlay.style.justifyContent = 'center';
        overlay.style.zIndex = '9999';
        overlay.innerHTML = `<div style="background:#fff;padding:20px;border-radius:8px;min-width:240px;text-align:center;">
      <div id="payment-processing-text" style="font-weight:700;margin-bottom:8px;">${text}</div>
      <div style="font-size:13px;color:#666">Keep this window open while payment completes.</div>
      <div style="margin-top:12px"><button id="payment-cancel-btn" style="padding:8px 12px;">Cancel</button></div>
    </div>`;
        document.body.appendChild(overlay);
        document.getElementById('payment-cancel-btn').addEventListener('click', () => {
            overlay.style.display = 'none';
        });
    } else {
        document.getElementById('payment-processing-text').textContent = text;
        overlay.style.display = 'flex';
    }
}

// Hide processing overlay (for polling flow)
function hideProcessingOverlay() {
    const overlay = document.getElementById('payment-processing-overlay');
    if (overlay) overlay.style.display = 'none';
}

// Helper function to poll for condition
async function pollForCondition(checkFn, options = {}) {
    const maxPolls = options.maxPolls || PAYMENT_POLL_CONFIG.maxPolls;
    const pollInterval = options.pollInterval || PAYMENT_POLL_CONFIG.pollInterval;
    const onSuccess = options.onSuccess || (() => {});
    const onTimeout = options.onTimeout || (() => {});
    const onError = options.onError || ((err) => console.error(err));
    
    let pollCount = 0;
    
    const poll = async () => {
        try {
            const result = await checkFn();
            
            if (result.success) {
                onSuccess(result.data);
                return;
            }
            
            if (pollCount >= maxPolls) {
                onTimeout();
                return;
            }
            
            pollCount++;
            setTimeout(poll, pollInterval);
        } catch (error) {
            if (pollCount >= maxPolls) {
                onError(error);
                onTimeout();
                return;
            }
            
            pollCount++;
            setTimeout(poll, pollInterval);
        }
    };
    
    await poll();
}

// Handle purchase credits
async function handlePurchaseCredits() {
    if (!tg || !tg.openInvoice) {
        if (tg) tg.showAlert('Payment feature is not available in this environment.');
        return;
    }

    // Show loading state
    elements.buyCreditsBtn.disabled = true;
    elements.buyCreditsBtn.innerHTML = 'Creating invoice...';

    try {
        // Get user ID from Telegram WebApp
        const userId = tg.initDataUnsafe?.user?.id || null;

        // Call serverless function to create invoice
        const response = await fetch('/api/create-invoice', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to create invoice');
        }

        // Open Telegram invoice
        tg.openInvoice(data.invoiceLink, async (status) => {
            if (status === 'paid') {
                const initialCredits = state.credits;
                
                console.log('Payment confirmed by Telegram, starting optimistic polling...');
                
                await pollForCondition(
                    async () => {
                        const response = await fetch(`/api/get-credits?userId=${userId}`);
                        const data = await response.json();
                        
                        // Check if credits increased (use >= to handle idempotency)
                        if (data.success && data.credits >= initialCredits + CREDITS_PURCHASE_AMOUNT) {
                            return { success: true, data: data.credits };
                        }
                        return { success: false };
                    },
                    {
                        onSuccess: (newCredits) => {
                            state.credits = newCredits;
                            updateCreditsDisplay();
                            if (tg) tg.showAlert(`Payment successful! New balance: ${newCredits} credits`);
                        },
                        onTimeout: async () => {
                            console.warn('Polling timed out, syncing status');
                            await syncCredits();
                            if (tg) tg.showAlert('Payment successful! Your credits have been updated.');
                        },
                        onError: (error) => {
                            console.error('Error polling for credits update:', error);
                        }
                    }
                );
            } else if (status === 'cancelled') {
                if (tg) tg.showAlert('Payment was cancelled.');
            } else if (status === 'failed') {
                if (tg) tg.showAlert('Payment failed. Please try again.');
            }
        });
    } catch (error) {
        console.error('Error creating invoice:', error);
        if (tg) tg.showAlert(`Failed to create payment: ${error.message}`);
    } finally {
        // Reset button state
        elements.buyCreditsBtn.disabled = false;
        elements.buyCreditsBtn.innerHTML = '10 Credits / 5 Stars';
    }
}

// Helper: Ask user to retry payment status check
async function askPaymentRetry() {
    if (tg && tg.showConfirm) {
        return new Promise(resolve => {
            tg.showConfirm('Payment is still being processed. Do you want to check again?', resolve);
        });
    } else {
        return confirm('Payment is still being processed. Do you want to check again?');
    }
}

// Helper: Manually check payment status
async function manuallyCheckPaymentStatus(userId) {
    try {
        const res = await fetch(`/api/get-credits?userId=${encodeURIComponent(userId)}`, { cache: 'no-cache' });
        const json = await res.json();
        if (json && json.success) {
            const isPro = json.isPro || json.is_pro || json.pro || false;
            updateCreditsUI({ credits: json.credits, isPro });
        }
    } catch (e) { 
        console.error(e); 
        if (tg) tg.showAlert('Unable to verify payment status. Please check your internet connection and reload the app.');
    }
}

// Helper: Handle payment confirmation polling with retry logic
async function handlePaymentConfirmation(userId, initialMessage = 'Confirming your Pro status...') {
    showProcessingOverlay(initialMessage);
    
    try {
        const result = await pollUserStatus(userId, {
            interval: 2000,
            timeout: 30000,
            onProgress: status => updateCreditsUI(status)
        });

        updateCreditsUI(result);
        hideProcessingOverlay();
        if (tg) tg.showAlert('Purchase confirmed — you now have unlimited credits!');
    } catch (pollErr) {
        hideProcessingOverlay();
        console.warn('Payment not confirmed in time', pollErr);
        
        const ok = await askPaymentRetry();
        if (ok) {
            await manuallyCheckPaymentStatus(userId);
        }
    }
}

// Handle Unlimited Credits purchase (using tg.openInvoice)
async function handleProUnlock() {
    if (!tg || !tg.openInvoice) {
        if (tg) tg.showAlert('Payment feature is not available in this environment.');
        return;
    }

    const userId = getCurrentUserId();
    if (!userId) {
        if (tg) tg.showAlert('User ID not available. Cannot start purchase flow.');
        return;
    }

    // Check if user already has Pro
    if (state.isPro) {
        if (tg) tg.showAlert('You already have Pro status with unlimited credits!');
        return;
    }

    // Show loading state
    elements.unlockProBtn.disabled = true;
    elements.unlockProBtn.innerHTML = 'Creating invoice...';

    try {
        const res = await fetch('/api/create-pro-invoice', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId })
        });
        const data = await res.json();

        if (!data || !data.success || !data.invoiceLink) {
            console.error('Invoice creation failed', data);
            if (tg) tg.showAlert('Could not create invoice. Try again later.');
            return;
        }

        // Use tg.openInvoice with callback
        tg.openInvoice(data.invoiceLink, async (status) => {
            try {
                if (status === 'paid') {
                    console.log('Payment confirmed by Telegram, starting polling...');
                    await handlePaymentConfirmation(userId);
                } else if (status === 'cancelled') {
                    console.log('Payment was cancelled by user');
                    if (tg) tg.showAlert('Payment was cancelled.');
                } else if (status === 'failed') {
                    console.log('Payment failed');
                    if (tg) tg.showAlert('Payment failed. Please try again.');
                }
            } catch (callbackErr) {
                console.error('Error in payment callback:', callbackErr);
                if (tg) tg.showAlert('An error occurred processing payment status.');
            }
        });
    } catch (err) {
        console.error('handleProUnlock error', err);
        if (tg) tg.showAlert('An error occurred creating invoice.');
    } finally {
        // Reset button state
        elements.unlockProBtn.disabled = false;
        elements.unlockProBtn.innerHTML = 'Unlimited / 100 Stars';
    }
}

function isDarkColor(hexColor) {
    if (typeof hexColor !== 'string') {
        return false;
    }

    const normalized = hexColor.trim().replace('#', '');
    if (!normalized) {
        return false;
    }

    const expanded = normalized.length === 3
        ? normalized.split('').map((char) => char + char).join('')
        : normalized;

    if (expanded.length !== 6 || /[^0-9a-fA-F]/.test(expanded)) {
        return false;
    }

    const red = parseInt(expanded.slice(0, 2), 16);
    const green = parseInt(expanded.slice(2, 4), 16);
    const blue = parseInt(expanded.slice(4, 6), 16);
    const luminance = (0.299 * red) + (0.587 * green) + (0.114 * blue);

    return luminance < 140;
}

// Apply Telegram theme
function applyTheme() {
    const themeParams = (tg && tg.themeParams) ? tg.themeParams : {};
    const isDarkTheme = (tg && tg.colorScheme === 'dark') || isDarkColor(themeParams.bg_color);

    document.documentElement.dataset.theme = isDarkTheme ? 'dark' : 'light';
    document.documentElement.style.setProperty('color-scheme', isDarkTheme ? 'dark' : 'light');
    document.documentElement.style.setProperty('--tg-theme-bg-color', themeParams.bg_color || (isDarkTheme ? '#16222b' : '#ffffff'));
    document.documentElement.style.setProperty('--tg-theme-text-color', themeParams.text_color || (isDarkTheme ? '#e4edf2' : '#000000'));
    document.documentElement.style.setProperty('--tg-theme-hint-color', themeParams.hint_color || (isDarkTheme ? '#8fa0aa' : '#999999'));
    document.documentElement.style.setProperty('--tg-theme-button-color', themeParams.button_color || (isDarkTheme ? '#2ca98b' : '#3390ec'));
    document.documentElement.style.setProperty('--tg-theme-button-text-color', themeParams.button_text_color || '#ffffff');
    document.documentElement.style.setProperty('--tg-theme-secondary-bg-color', themeParams.secondary_bg_color || (isDarkTheme ? '#1b2a34' : '#f0f0f0'));

    // Log ready state
    console.log('Telegram WebApp initialized:', {
        version: tg ? tg.version : 'mock',
        platform: tg ? tg.platform : 'web',
        colorScheme: tg ? tg.colorScheme : 'light'
    });
}

// Setup event listeners
function setupEventListeners() {
    if (elements.drawTabBtn) {
        elements.drawTabBtn.addEventListener('click', () => {
            setActiveTab('draw');
        });
    }

    if (elements.verifyTabBtn) {
        elements.verifyTabBtn.addEventListener('click', () => {
            setActiveTab('verify');
        });
    }

    // Handle name input changes
    elements.namesInput.addEventListener('input', () => {
        const rawNames = elements.namesInput.value
            .replace(/\r\n?/g, '\n')
            .split('\n')
            .map(name => name.trim())
            .filter(name => name.length > 0);
        const allNames = rawNames.slice(0, MAX_NAMES);
        
        // Limit to MAX_NAMES
        state.names = allNames.slice(0, MAX_NAMES);
        
        // Show warning if too many names
        if (rawNames.length > MAX_NAMES) {
            elements.nameCount.textContent = `${state.names.length} (${rawNames.length - MAX_NAMES} ignored)`;
        } else {
            elements.nameCount.textContent = state.names.length;
        }
        
        drawWheel();
        updateSpinButton();
    });

    if (elements.importFileBtn && elements.importFileInput) {
        elements.importFileBtn.addEventListener('click', () => {
            elements.importFileInput.click();
        });

        elements.importFileInput.addEventListener('change', async (event) => {
            const file = event.target.files?.[0];
            await handleImportFromFile(file);
        });
    }

    if (elements.importClipboardBtn) {
        elements.importClipboardBtn.addEventListener('click', handleImportFromClipboard);
    }

    if (elements.provablyFairToggle) {
        elements.provablyFairToggle.addEventListener('change', () => {
            state.provablyFairEnabled = Boolean(elements.provablyFairToggle.checked);
            if (!state.provablyFairEnabled) {
                renderProvablyFairStatus(null);
            }
            updateSpinButton();
        });
    }

    if (elements.publicEntrantsToggle) {
        elements.publicEntrantsToggle.addEventListener('change', () => {
            state.publicEntrantsEnabled = Boolean(elements.publicEntrantsToggle.checked);
        });
    }
    
    // Handle pick count change
    elements.pickCount.addEventListener('input', () => {
        let value = parseInt(elements.pickCount.value, 10);
        
        // Validate and clamp
        if (isNaN(value) || value < 1) {
            value = 1;
        }
        if (value > MAX_WINNERS) {
            value = MAX_WINNERS;
            elements.pickCount.value = MAX_WINNERS;
        }
        if (value > state.names.length && state.names.length > 0) {
            // Don't auto-correct, but update button to show error
        }
        
        // Update pointer visibility based on pick count
        if (elements.wheelPointer) {
            elements.wheelPointer.style.display = value === 1 ? 'block' : 'none';
        }
        
        updateSpinButton();
    });
    
    // Handle spin button
    elements.spinBtn.addEventListener('click', handleSpin);
    
    // Handle copy button
    elements.copyBtn.addEventListener('click', handleCopy);
    
    // Handle share button
    elements.shareBtn.addEventListener('click', handleShare);
    
    // Handle pick again button
    elements.pickAgainBtn.addEventListener('click', handlePickAgain);
    
    // Handle remove winners button
    elements.removeWinnersBtn.addEventListener('click', handleRemoveWinners);
    
    // Handle start fresh button
    elements.startFreshBtn.addEventListener('click', handleStartFresh);
    
    // Handle send to chat button
    elements.sendToChatBtn.addEventListener('click', handleSendToChat);

    if (elements.toggleProofBtn) {
        elements.toggleProofBtn.addEventListener('click', handleToggleProofJson);
    }

    if (elements.copyProofBtn) {
        elements.copyProofBtn.addEventListener('click', handleCopyProofJson);
    }

    if (elements.replayDrawBtn) {
        elements.replayDrawBtn.addEventListener('click', handleReplayDraw);
    }

    if (elements.verifyReceiptBtn) {
        elements.verifyReceiptBtn.addEventListener('click', handleVerifyReceiptLookup);
    }

    if (elements.verifyReceiptInput) {
        elements.verifyReceiptInput.addEventListener('input', () => {
            const upper = String(elements.verifyReceiptInput.value || '').toUpperCase().replace(/[^A-Z0-9-]/g, '');
            elements.verifyReceiptInput.value = upper;
        });

        elements.verifyReceiptInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                handleVerifyReceiptLookup();
            }
        });
    }
    
    // Handle watch ad button
    elements.watchAdBtn.addEventListener('click', showAdsgramAd);
    
    // Handle buy credits button
    elements.buyCreditsBtn.addEventListener('click', handlePurchaseCredits);
    
    // Handle Unlimited Credits button (if exists)
    if (elements.unlockProBtn) {
        elements.unlockProBtn.addEventListener('click', handleProUnlock);
    }
}

// Initialize app
async function initApp() {
    await initCredits();
    trackAnalyticsEvent('app_open');
    setupEventListeners();
    applyTheme();
    if (tg && typeof tg.onEvent === 'function') {
        tg.onEvent('themeChanged', applyTheme);
    }
    drawWheel();
    setActiveTab('draw');
    if (elements.publicEntrantsToggle) {
        state.publicEntrantsEnabled = Boolean(elements.publicEntrantsToggle.checked);
    }
    updateSpinButton();
    
    // Initial status sync on page load (using polling flow helpers)
    const userId = getCurrentUserId();
    if (userId) {
        try {
            const res = await fetch(`/api/get-credits?userId=${encodeURIComponent(userId)}`, { cache: 'no-cache' });
            const json = await res.json();
            if (json && json.success) {
                const isPro = json.isPro || json.is_pro || json.pro || false;
                updateCreditsUI({ credits: json.credits, isPro });
            }
        } catch (err) {
            console.warn('Initial credit sync failed', err);
        }
    }

    const launchReceipt = getReceiptCodeFromLaunchContext();
    if (launchReceipt && elements.verifyReceiptInput) {
        setActiveTab('verify');
        elements.verifyReceiptInput.value = launchReceipt;
        await handleVerifyReceiptLookup();
    }
}

// Start app if Telegram is already loaded
if (tg) {
    initApp();
}
